import {
  DriverDocumentSorting,
  DriverPayrollSorting,
  DriverSorting,
  InvoiceSorting,
  LiveRoutesSorting,
  LocationScanCodesSorting,
  PricingRulesSorting,
  ShipmentTemplatesSorting,
  ShipmentsSorting,
  EndCustomerSorting,
  AddressBookSorting,
  PayrollDeductionRuleSorting,
  SurchargePricingRuleSorting,
} from "@api/graphql/generated/generated-types";
import { isEqual } from "lodash";
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react";

export type SortFieldsType =
  | ShipmentsSorting[]
  | PricingRulesSorting[]
  | ShipmentTemplatesSorting[]
  | DriverPayrollSorting[]
  | DriverSorting[]
  | InvoiceSorting[]
  | EndCustomerSorting[]
  | AddressBookSorting[]
  | PayrollDeductionRuleSorting[]
  | SurchargePricingRuleSorting[]
  | LocationScanCodesSorting[]
  | LiveRoutesSorting[]
  | DriverDocumentSorting[];
interface ContextState {
  sortFields: SortFieldsType;
  setSortFields: (sortFields: ShipmentsSorting[]) => void;
  setFilters: any;
  filters: any[];
}

export type Filter = {
  id: string | number;
  type: any;
  value: any;
};

const TableControl = createContext<ContextState | undefined>(undefined);

type Props = {
  children: React.ReactNode;
  sortKey: string; // Unique key for the sort fields
};

// Custom hook for deep comparison
const useDeepCompareMemoize = <T,>(value: T) => {
  const ref = useRef<T>();
  if (!isEqual(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
};

export const TableControlContext = ({ children, sortKey }: Props) => {
  // Read initial value from localStorage (only once on mount)
  const getInitialSortFields = (): ShipmentsSorting[] => {
    if (typeof window === "undefined") return [];
    try {
      const stored = localStorage.getItem(`${sortKey}-sortFields`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  // Use useState instead of useLocalStorage to avoid sync conflicts
  const [sortFields, setSortFields] = useState<ShipmentsSorting[]>(getInitialSortFields);

  // State for filters
  const [filters, setFilters] = useState<Filter[]>([]);

  // Persist to localStorage whenever sortFields changes (without causing re-sync)
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(`${sortKey}-sortFields`, JSON.stringify(sortFields));
    }
  }, [sortFields, sortKey]);

  // Deep compare memoization for sortFields and filters
  const memoizedSortFields = useDeepCompareMemoize(sortFields);
  const memoizedFilters = useDeepCompareMemoize(filters);

  // Context value memoized to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      sortFields: memoizedSortFields || [],
      setSortFields,
      filters: memoizedFilters || [],
      setFilters,
    }),
    [memoizedSortFields, memoizedFilters]
  );

  return (
    <TableControl.Provider value={contextValue}>
      {children}
    </TableControl.Provider>
  );
};

// Custom hook for using the context
export const useTableControl = () => {
  const context = useContext(TableControl);
  if (context === undefined) {
    throw new Error(
      "useTableControl must be used within a TableControlContext"
    );
  }
  return context;
};
